/** @typedef {'success'|'partial'|'missed'|'relapse'|null} DayStatus */

/**
 * @typedef {Object} StreakRun
 * @property {string} id
 * @property {number} attemptNumber 1-based
 * @property {number} days consecutive success days in this run
 * @property {string} startDate YYYY-MM-DD first success
 * @property {string} endDate YYYY-MM-DD last success day, or fail date if endedHow=fail
 * @property {'fail'|'ongoing'|'gap'|'missed'|'partial'} endedHow
 * @property {string} [trigger] when endedHow is fail
 */

/**
 * @param {string} key YYYY-MM-DD
 * @returns {number|null}
 */
export function toDayIndex(key) {
  const [y, m, d] = String(key).split("-").map((v) => Number(v));
  if (!y || !m || !d) return null;
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
}

/**
 * @param {number} idx
 * @returns {string}
 */
export function dayIndexToKey(idx) {
  const d = new Date(idx * 86400000);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * @param {Record<string, { status?: string, relapse?: { trigger?: string }, selectedColor?: string }>} markedDates
 * @param {string} key
 * @returns {DayStatus}
 */
export function inferDayStatus(markedDates, key) {
  const entry = markedDates?.[key];
  if (!entry || typeof entry !== "object") return null;
  const s = entry.status;
  if (s === "success" || s === "partial" || s === "missed" || s === "relapse") return s;
  if (entry.relapse) return "relapse";
  if (entry.selectedColor === "#E74C3C") return "relapse";
  if (entry.selectedColor === "#1ABC9C") return "success";
  return "missed";
}

/**
 * Consecutive success days ending the day before `failDateKey` (not including fail day).
 * @param {Record<string, unknown>} markedDates
 * @param {string} failDateKey
 */
export function streakDaysBeforeDate(markedDates, failDateKey) {
  const failIdx = toDayIndex(failDateKey);
  if (failIdx === null) return 0;
  let streak = 0;
  for (let idx = failIdx - 1; idx >= failIdx - 10000; idx--) {
    const key = dayIndexToKey(idx);
    if (inferDayStatus(markedDates, key) === "success") streak += 1;
    else break;
  }
  return streak;
}

/**
 * Build streak runs from calendar history (source of truth for the chart).
 * @param {Record<string, { status?: string, relapse?: { trigger?: string } }>} markedDates
 * @param {string} [todayKey]
 * @returns {StreakRun[]}
 */
export function buildStreakRuns(markedDates, todayKey = new Date().toISOString().split("T")[0]) {
  const keys = Object.keys(markedDates ?? {});
  const todayIdx = toDayIndex(todayKey);
  if (todayIdx === null) return [];

  const indices = keys.map((k) => toDayIndex(k)).filter((i) => i !== null);
  if (indices.length === 0) return [];

  const minIdx = Math.min(...indices);
  const maxIdx = Math.max(todayIdx, ...indices);

  /** @type {StreakRun[]} */
  const runs = [];
  let runStart = null;
  let runDays = 0;
  let lastSuccessKey = null;

  const flushRun = (endDate, endedHow, trigger) => {
    if (runDays <= 0 || !runStart || !lastSuccessKey) return;
    runs.push({
      id: `run_${runs.length}`,
      attemptNumber: runs.length + 1,
      days: runDays,
      startDate: runStart,
      endDate: endedHow === "fail" ? endDate : lastSuccessKey,
      endedHow,
      trigger,
    });
    runStart = null;
    runDays = 0;
    lastSuccessKey = null;
  };

  for (let idx = minIdx; idx <= maxIdx; idx++) {
    const key = dayIndexToKey(idx);
    const status = inferDayStatus(markedDates, key);

    if (status === "success") {
      if (runDays === 0) runStart = key;
      runDays += 1;
      lastSuccessKey = key;
      continue;
    }

    if (status === "relapse") {
      flushRun(key, "fail", markedDates[key]?.relapse?.trigger);
      continue;
    }

    if (runDays > 0) {
      const how = status === "partial" ? "partial" : status === "missed" ? "missed" : "gap";
      flushRun(lastSuccessKey, how, undefined);
    }
  }

  if (runDays > 0 && runStart && lastSuccessKey) {
    runs.push({
      id: `run_${runs.length}`,
      attemptNumber: runs.length + 1,
      days: runDays,
      startDate: runStart,
      endDate: lastSuccessKey,
      endedHow: "ongoing",
    });
  }

  return runs.map((r, i) => ({ ...r, attemptNumber: i + 1, id: `run_${i}` }));
}

/**
 * @param {StreakRun[]} runs
 */
export function getTotalFailAttempts(runs) {
  return runs.filter((r) => r.endedHow === "fail").length;
}
